/**
 * The garage: what you can drive.
 *
 * Every tank is the same rules with the numbers moved. Armour, drive, how
 * fast the hull turns, how quick the gun is, what a shell takes off, whether
 * it bounces on its own. Standard is the one the
 * game was balanced on; the rest are earned by doing something particular
 * in it, and each is a different way to play the same ten yards.
 */

import { once, type Progressed, type Stats } from "./mods.ts";

export type TankId = "standard" | "scout" | "bulwark" | "skipper" | "wraith";

export type TankSpec = {
  id: TankId;
  name: string;
  blurb: string;
  /** What earns it, as shown while it is still locked. */
  unlock: string;
  earned: (s: Stats) => boolean;
  progress: (s: Stats) => Progressed;
  /** Armour. */
  hp: number;
  /** Multipliers on the Standard tank. */
  drive: number;
  turn: number;
  cooldown: number;
  /** Added to every shell's damage. */
  damage: number;
  /** Ricochets every shell gets for free. */
  bounces: number;
  /** Hull size against Standard. Bigger is easier to hit. */
  size: number;
  colour: string;
};

export const TANKS: TankSpec[] = [
  {
    id: "standard",
    name: "Standard",
    blurb: "Balanced. 6 armour.",
    unlock: "Always unlocked",
    earned: () => true,
    progress: () => once(true),
    hp: 6, drive: 1, turn: 1, cooldown: 1, damage: 0, bounces: 0, size: 1,
    colour: "#02602a",
  },
  {
    id: "scout",
    name: "Scout",
    blurb: "Small and fast, quick gun. 4 armour.",
    unlock: "Clear 3 levels in a run",
    earned: (s) => s.cleared >= 3,
    progress: (s) => ({ value: Math.min(3, s.cleared), target: 3 }),
    hp: 4, drive: 1.3, turn: 1.25, cooldown: 0.72, damage: 0, bounces: 0, size: 0.86,
    colour: "#176d3c",
  },
  {
    id: "bulwark",
    name: "Bulwark",
    blurb: "Slow and wide, +1 damage. 9 armour.",
    unlock: "Fire the Kitchen Sink",
    earned: (s) => s.sink,
    progress: (s) => once(s.sink),
    hp: 9, drive: 0.78, turn: 0.7, cooldown: 1.25, damage: 1, bounces: 0, size: 1.18,
    colour: "#2f4f3a",
  },
  {
    id: "skipper",
    name: "Skipper",
    blurb: "Every shell bounces once. 5 armour.",
    unlock: "100 ricochets",
    earned: (s) => s.bounces >= 100,
    progress: (s) => ({ value: Math.min(100, s.bounces), target: 100 }),
    hp: 5, drive: 1.05, turn: 1.1, cooldown: 0.95, damage: 0, bounces: 1, size: 0.95,
    colour: "#0b6f7a",
  },
  {
    id: "wraith",
    name: "Wraith",
    blurb: "Fastest tank, quickest gun. 3 armour.",
    unlock: "Land a ×5 combo",
    earned: (s) => s.combo5,
    progress: (s) => once(s.combo5),
    hp: 3, drive: 1.45, turn: 1.4, cooldown: 0.62, damage: 0, bounces: 0, size: 0.9,
    colour: "#3a3a34",
  },
];

export const tankById = (id: string): TankSpec => TANKS.find((t) => t.id === id) ?? (TANKS[0] as TankSpec);
